import React, { useRef, useState } from 'react'
import classes from '../styles.module.scss'
import { createCn } from '../../../helpers/utils'
import { Meeting } from '../files.page';
import { ClockIcon, FlaskIcon, MusicIcon, TrashIcon, WaveformIcon } from '../../../assets';
import { useLocation, useNavigate } from 'react-router-dom';
import Modal from '../../../components/modal/modal.component';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { apiService, MeetingProgress } from '../../../services/api.service';
import { FileVisualType, getFileSourceTag, getFileVisualType } from '../utils/file-type';
import WaveformBars from './waveform-bars.component';
import RowProgress from './row-progress.component';
import { DraggableProvidedDragHandleProps, DraggableProvidedDraggableProps } from '@hello-pangea/dnd';

interface IProps {
  item: Meeting;
  setList: React.Dispatch<React.SetStateAction<Meeting[]>>;
  type?: 'files' | 'favorites' | 'trash';
  viewMode?: 'list' | 'grid';
  index?: number;
  showDragHandle?: boolean;
  innerRef?: (element: HTMLElement | null) => void;
  draggableProps?: DraggableProvidedDraggableProps;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isDragging?: boolean;
  /** Задача L1: живой прогресс для записей в статусе processing (см. useMeetingsProgress). */
  progress?: MeetingProgress;
}

const cn = createCn('files', classes)

const parseMeetingDate = (value: string) => {
  const [day, month, year] = (value || '').split('.').map(Number);
  if (!day || !month || !year) return null;
  return new Date(year, month - 1, day);
};

function FileTypeIcon({ visualType }: { visualType: FileVisualType }) {
  const icons: Record<FileVisualType, React.ReactNode> = {
    audio: <MusicIcon />,
    diarization: <WaveformIcon />,
    test: <FlaskIcon />,
    zoom: <MusicIcon />,
  };
  return (
    <div className={cn('file-icon', { [visualType]: true })}>
      {icons[visualType]}
    </div>
  );
}

const FileRow: React.FC<IProps> = ({
  item,
  setList,
  type = 'files',
  viewMode = 'list',
  index = 0,
  showDragHandle = false,
  innerRef,
  draggableProps,
  dragHandleProps = null,
  isDragging = false,
  progress,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openDelete, setOpenDelete] = useState(false);
  const location = useLocation();
  const skipClickRef = useRef(false);

  const handleProceedToMeeting = (id: string) => {
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }
    navigate(`/files/${id}`, { state: { file: item, returnUrl: location.pathname } });
  }

  const handleDelete = async (id: string) => {
    try {
      await apiService.deleteMeeting(id);
    } catch {
      // keep local removal on API error for trash UX
    }
    setList(prevList => prevList.filter(row => row.id !== id));
    toast(
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <TrashIcon />
        {type === 'trash' ? t('file.restore-success') : t('file.delete-modal.success')}
      </div>,
    );
  }

  const meetingDate = parseMeetingDate(item.date);
  const isNew = meetingDate
    ? (Date.now() - meetingDate.getTime()) / (1000 * 60 * 60 * 24) <= 30
    : false;
  const visualType = getFileVisualType(item.name);
  const sourceTag = getFileSourceTag(item.name);
  const isProcessing = item.status === 'processing';
  const isError = item.status === 'error'
    || (item.duration === '00:00:00' && !isProcessing && item.status !== 'stored');
  const statusLabel = isError
    ? t('files.status-error')
    : isProcessing
      ? t('files.status-processing')
      : t('files.status-processed');
  const statusVariant = isError ? 'error' : isProcessing ? 'processing' : 'processed';
  const yearLabel = meetingDate?.getFullYear() || '';
  const showWaveform = !isError && !isProcessing && viewMode === 'list';

  React.useEffect(() => {
    if (isDragging) skipClickRef.current = true;
  }, [isDragging]);

  return (
    <>
      <article
        ref={innerRef}
        {...draggableProps}
        className={cn('card', {
          trash: type === 'trash',
          grid: viewMode === 'grid',
          featured: index === 0 && type === 'files' && !isDragging,
          dragging: isDragging,
        })}
        style={{
          ...draggableProps?.style,
          animationDelay: isDragging ? undefined : `${index * 0.07}s`,
        }}
        onClick={() => handleProceedToMeeting(item.id)}
      >
        {showDragHandle && dragHandleProps && (
          <div
            className={cn('drag-handle')}
            aria-label={t('files.drag-reorder')}
            title={t('files.drag-reorder')}
            {...dragHandleProps}
          >
            <span className={cn('drag-handle-bars')} aria-hidden />
          </div>
        )}
        <div className={cn('card-main')}>
          <FileTypeIcon visualType={visualType} />
          <div className={cn('details')}>
            <div className={cn('name-row')}>
              <span className={cn('name')}>{item.name}</span>
              {isNew && <span className={cn('new-badge')}>{t('files.badge-new')}</span>}
              {item.sharedOwnerEmail && (
                <span className={cn('access-badge')} title={item.sharedOwnerEmail}>
                  {t('files.access-badge')} · {item.sharedOwnerEmail}
                </span>
              )}
            </div>
            <div className={cn('meta-row')}>
              <span className={cn('status', { [statusVariant]: true })}>
                <span className={cn('status-dot', { [statusVariant]: true })} />
                {statusLabel}
              </span>
              {yearLabel && <span className={cn('meta-sep')}>·</span>}
              {yearLabel && <span className={cn('year')}>{yearLabel}</span>}
              <span className={cn('meta-sep')}>·</span>
              <span className={cn('source-tag')}>{sourceTag}</span>
            </div>
          </div>
        </div>
        {showWaveform && <WaveformBars />}
        {isProcessing && <RowProgress progress={progress} />}
        <div className={cn('card-actions')}>
          <div className={cn('duration-block')}>
            <strong className={cn('duration-value')}>{item.duration || '—'}</strong>
            <span className={cn('duration-label')}>
              <ClockIcon />
              {t('files.duration')}
            </span>
          </div>
          {type !== 'trash' && (
            <button
              type="button"
              className={cn('delete-btn')}
              aria-label={t('file.delete')}
              title={t('file.delete')}
              onClick={(e) => {
                e.stopPropagation();
                setOpenDelete(true);
              }}
            >
              <TrashIcon />
            </button>
          )}
        </div>
      </article>

      {type === 'trash' && (
        <div className={cn('restore-row')}>
          <button type="button" onClick={() => handleDelete(item.id)}>{t('action.restore')}</button>
        </div>
      )}

      <Modal
        show={openDelete}
        handleClose={() => setOpenDelete(false)}
        style={{ padding: 0, minHeight: 'auto', borderRadius: '1rem' }}
        footer={
          <div className={cn('delete-modal__footer')}>
            <div onClick={() => setOpenDelete(false)}>{t('action.cancel')}</div>
            <div onClick={() => { handleDelete(item.id); setOpenDelete(false); }}>
              {t('action.delete')}
            </div>
          </div>
        }
      >
        <div className={cn('delete-modal')}>
          <div className={cn('delete-modal__header')}>
            <TrashIcon />
            {t('file.delete-modal.heading')}
          </div>
          <p>{t('file.delete-modal.text')}</p>
        </div>
      </Modal>
    </>
  );
};

export default FileRow;
